import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtNum } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [readyVariantId, setReadyVariantId] = useState("");
  const [qty, setQty] = useState(1);
  const [orderNote, setOrderNote] = useState("");
  const [delivery, setDelivery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Custom-cut modal state
  const [customModal, setCustomModal] = useState(null); // selected width-variant
  const [customLength, setCustomLength] = useState("");
  const [customShape, setCustomShape] = useState("");
  const [customEdge, setCustomEdge] = useState("");
  const [customFringe, setCustomFringe] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [customSubmitting, setCustomSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/dealer/products/${id}`).then(({ data }) => {
      setP(data);
      const firstReady = (data.variants || []).find((v) => v.variant_type === "READY_SIZE" && v.stock_amount > 0);
      if (firstReady) setReadyVariantId(firstReady.id);
    });
    api.get("/dealer/profile").then(({ data }) => {
      setDelivery(data.company.main_delivery_address || "");
    });
  }, [id]);

  const readyVariants = useMemo(() => (p?.variants || []).filter((v) => v.variant_type === "READY_SIZE"), [p]);
  const customVariants = useMemo(() => (p?.variants || []).filter((v) => v.variant_type === "WIDTH_OPTION"), [p]);
  const readySelected = readyVariants.find((v) => v.id === readyVariantId);

  const allDeliveryOptions = useMemo(() => {
    const c = user?.company;
    if (!c) return [];
    const arr = [c.main_delivery_address];
    (c.extra_delivery_addresses || []).forEach((a) => arr.push(`${a.label}: ${a.address}`));
    return arr;
  }, [user]);

  if (!p) return <div className="text-[#6b655c]">Yükleniyor…</div>;

  // --- Hazır Ebat sipariş ---
  const submitReady = async () => {
    if (!readySelected) return toast.error("Ebat seçin");
    const nQty = parseInt(qty, 10);
    if (!nQty || nQty <= 0) return toast.error("Adet 1 veya üzeri olmalı");
    if (!delivery) return toast.error("Teslimat adresi seçin");

    setSubmitting(true);
    try {
      const { data } = await api.post("/dealer/orders", {
        delivery_address: delivery,
        note: orderNote,
        items: [{
          product_id: p.id,
          variant_id: readySelected.id,
          quantity: nQty,
          length_meter: null,
          special_processing: null,
          line_note: "",
        }],
      });
      toast.success(`Sipariş oluşturuldu: ${data.order_no}`);
      setQty(1);
      nav(`/bayi/siparislerim/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Özel Kesim modalı ---
  const openCustomModal = (v) => {
    if (v.stock_amount <= 0) {
      toast.error("Bu en seçeneğinde stok yok");
      return;
    }
    setCustomModal(v);
    setCustomLength("");
    setCustomShape("");
    setCustomEdge("");
    setCustomFringe("");
    setCustomNote("");
  };

  const customSqm = customModal && customLength
    ? (customModal.width_cm / 100) * (parseFloat(customLength || 0) / 100)
    : 0;

  const submitCustom = async () => {
    if (!customModal) return;
    const lenCm = parseFloat(customLength);
    const stockCm = customModal.stock_amount * 100;
    if (!lenCm || lenCm <= 0) return toast.error("Boy (cm) girin");
    if (lenCm > stockCm) {
      return toast.error(
        `Stok yetersiz! Bu en için en fazla ${fmtNum(stockCm)} cm sipariş verebilirsiniz. Lütfen daha düşük bir değer girin.`
      );
    }
    if (!customShape) return toast.error("Şekil seçimi zorunludur");
    if (!customEdge) return toast.error("Kenar dokuma seçimi zorunludur");
    if (customEdge === "Saçak" && !customFringe) return toast.error("Saçak türü seçimi zorunludur");
    if (!delivery) return toast.error("Teslimat adresi seçin");

    setCustomSubmitting(true);
    try {
      const { data } = await api.post("/dealer/orders", {
        delivery_address: delivery,
        note: orderNote,
        items: [{
          product_id: p.id,
          variant_id: customModal.id,
          quantity: 1,
          length_meter: lenCm / 100,
          special_processing: {
            shape: customShape,
            edge: customEdge,
            fringe_type: customEdge === "Saçak" ? customFringe : null,
            note: customNote,
          },
          line_note: "",
        }],
      });
      toast.success(`Sipariş oluşturuldu: ${data.order_no}`);
      setCustomModal(null);
      nav(`/bayi/siparislerim/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setCustomSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="dealer-product-detail">
      <Link to="/bayi/urunler" className="inline-flex items-center gap-2 text-sm text-[#6b655c] hover:text-[#c8a97e]">
        <ArrowLeft size={14}/> Ürünlere dön
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
        {/* Image */}
        <div>
          <div className="aspect-[4/5] bg-[#eae5dc] overflow-hidden">
            {p.cover_image ? (
              <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#6b655c]">Görsel yok</div>
            )}
          </div>
          {p.gallery_images?.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {p.gallery_images.map((g, i) => (
                <img key={i} src={g} alt="" className="aspect-square object-cover" />
              ))}
            </div>
          )}
        </div>

        {/* Info & order sections */}
        <div className="space-y-8">
          <div>
            {p.quality_name && <span className="label-tag">{p.quality_name}</span>}
            <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">{p.name}</h1>
            <p className="text-[#3d3a35] mt-4 leading-relaxed">{p.long_description || p.short_description}</p>
          </div>

          {/* Teslimat */}
          <div>
            <div className="label-tag mb-2">Teslimat Adresi</div>
            <Select value={delivery} onValueChange={setDelivery}>
              <SelectTrigger data-testid="delivery-select"><SelectValue placeholder="Adres seç"/></SelectTrigger>
              <SelectContent>
                {allDeliveryOptions.map((a, i) => <SelectItem key={i} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Hazır Ebat Section */}
          {readyVariants.length > 0 && (
            <div className="border border-[#eae5dc] bg-white p-5 md:p-6" data-testid="ready-section">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="font-serif text-2xl">Hazır Ebat Ölçüler</h2>
                  <p className="text-xs text-[#6b655c] mt-1">Hazır ebat → adet seçin → doğrudan sipariş.</p>
                </div>
                <div className="flex items-center gap-3 text-[0.65rem] uppercase tracking-wider text-[#6b655c]">
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-600"/>Stokta</span>
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#a09a8e]"/>Tükendi</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {readyVariants.map((v) => {
                  const inStock = v.stock_amount > 0;
                  const selected = readyVariantId === v.id;
                  const low = inStock && v.stock_amount <= 3;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => inStock && setReadyVariantId(v.id)}
                      disabled={!inStock}
                      data-testid={`ready-variant-btn-${v.id}`}
                      className={`relative text-left transition-all border min-w-[110px] px-4 py-2.5 ${
                        selected
                          ? "bg-[#1a1a1a] text-[#f7f5f0] border-[#1a1a1a] shadow-md"
                          : inStock
                          ? "bg-white border-[#c8a97e]/50 hover:border-[#c8a97e] hover:bg-[#fdf9f1]"
                          : "bg-[#efeae0] border-dashed border-[#cfc8b9] text-[#a09a8e] cursor-not-allowed"
                      }`}
                    >
                      {!inStock && <span className="absolute -top-1.5 -right-1.5 bg-[#a09a8e] text-[#f7f5f0] text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5">Tükendi</span>}
                      {low && !selected && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5">Az</span>}
                      <div className={`font-medium text-sm ${!inStock ? "line-through" : ""}`}>{v.label}</div>
                      <div className={`text-[0.65rem] mt-1 flex items-center gap-1 ${selected ? "text-[#c8a97e]" : inStock ? "text-emerald-700" : "text-[#a09a8e]"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${selected ? "bg-[#c8a97e]" : inStock ? "bg-emerald-600" : "bg-[#a09a8e]"}`}/>
                        {inStock ? <>Stok: {fmtNum(v.stock_amount)} ad</> : "Stokta yok"}
                      </div>
                    </button>
                  );
                })}
              </div>

              {readySelected && (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                  <div>
                    <div className="label-tag mb-2">Adet</div>
                    <Input
                      type="number"
                      min={1}
                      value={qty}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") { setQty(""); return; }
                        const n = parseInt(raw, 10);
                        setQty(isNaN(n) || n < 1 ? 1 : n);
                      }}
                      onBlur={() => { if (!qty || qty < 1) setQty(1); }}
                      data-testid="qty-input"
                    />
                  </div>
                  <div className="bg-[#f7f5f0] border border-[#eae5dc] p-3 text-sm">
                    <div className="text-xs uppercase tracking-wider text-[#6b655c]">Ebat</div>
                    <div className="font-medium">{readySelected.width_cm}×{readySelected.height_cm} cm</div>
                  </div>
                  <div className="bg-[#fdf9f1] border border-[#c8a97e]/40 p-3 text-sm">
                    <div className="text-xs uppercase tracking-wider text-[#c8a97e]">Toplam m²</div>
                    <div className="font-serif text-2xl">{fmtNum((readySelected.width_cm * readySelected.height_cm / 10000) * (qty || 0))}</div>
                  </div>
                </div>
              )}
              {readySelected && (
                <Button onClick={submitReady} disabled={submitting} className="btn-luxe mt-5 w-full sm:w-auto" data-testid="submit-ready-btn">
                  {submitting ? <Loader2 className="animate-spin" size={14}/> : "Sipariş Ver"}
                </Button>
              )}
            </div>
          )}

          {/* Özel Kesim Section */}
          {customVariants.length > 0 && (
            <div className="border border-[#eae5dc] bg-white p-5 md:p-6" data-testid="custom-section">
              <div className="mb-4">
                <h2 className="font-serif text-2xl">Özel Kesim Ölçüler</h2>
                <p className="text-xs text-[#6b655c] mt-1">En seçin → boy ve özel işlem detaylarını doldurun → sipariş.</p>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {customVariants.map((v) => {
                  const inStock = v.stock_amount > 0;
                  const low = inStock && v.stock_amount * 100 <= 1000;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => openCustomModal(v)}
                      disabled={!inStock}
                      data-testid={`custom-variant-btn-${v.id}`}
                      className={`relative text-left transition-all border min-w-[130px] px-4 py-3 ${
                        inStock
                          ? "bg-white border-[#c8a97e]/50 hover:border-[#1a1a1a] hover:bg-[#fdf9f1]"
                          : "bg-[#efeae0] border-dashed border-[#cfc8b9] text-[#a09a8e] cursor-not-allowed"
                      }`}
                    >
                      {!inStock && <span className="absolute -top-1.5 -right-1.5 bg-[#a09a8e] text-[#f7f5f0] text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5">Tükendi</span>}
                      {low && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5">Az</span>}
                      <div className={`font-medium ${!inStock ? "line-through" : ""}`}>{v.label}</div>
                      <div className={`text-[0.65rem] mt-1 flex items-center gap-1 ${inStock ? "text-emerald-700" : "text-[#a09a8e]"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${inStock ? "bg-emerald-600" : "bg-[#a09a8e]"}`}/>
                        {inStock ? <>Stok: {fmtNum(v.stock_amount * 100)} cm</> : "Stokta yok"}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#c8a97e] uppercase tracking-wider mt-4">
                ⚡ En seçince özel işlem detayları (şekil/kenar/saçak + boy) için pop-up açılır.
              </p>
            </div>
          )}

          {/* Sipariş Notu (genel) */}
          <div>
            <div className="label-tag mb-2">Sipariş Notu (opsiyonel)</div>
            <Textarea rows={2} value={orderNote} onChange={(e) => setOrderNote(e.target.value)} data-testid="order-note-input"/>
          </div>
        </div>
      </div>

      {/* Özel Kesim Modal */}
      <Dialog open={!!customModal} onOpenChange={(o) => !o && setCustomModal(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Özel Kesim Sipariş — {customModal?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <SegOpt label="A. Şekil Seçiniz *" options={["Düz", "Oval", "Daire"]} value={customShape} onChange={setCustomShape} testid="modal-shape" />
            <SegOpt
              label="B. Kenar Dokuma Seçiniz *"
              options={["Saçak", "Overlok", "Katlama", "İşlemsiz"]}
              value={customEdge}
              onChange={(v) => {
                setCustomEdge(v);
                if (v !== "Saçak") setCustomFringe("");
              }}
              testid="modal-edge"
            />
            {customEdge === "Saçak" && (
              <SegOpt
                label="C. Saçak Türü Seçiniz *"
                options={["Orijinal", "Spor"]}
                value={customFringe}
                onChange={setCustomFringe}
                testid="modal-fringe"
              />
            )}
            <div>
              <div className="label-tag mb-2">D. Açıklama (max 250)</div>
              <Textarea rows={3} maxLength={250} value={customNote} onChange={(e) => setCustomNote(e.target.value)} data-testid="modal-note-input" />
              <div className="text-xs text-[#6b655c] mt-1 text-right">{customNote.length}/250</div>
            </div>
            <div>
              <div className="label-tag mb-2">E. En × Boy</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">En (cm)</label>
                  <Input
                    value={customModal?.width_cm || ""}
                    readOnly
                    disabled
                    className="bg-[#f0ebe2] text-[#1a1a1a] cursor-not-allowed font-medium"
                    data-testid="modal-en-input"
                  />
                  <p className="text-[0.65rem] text-[#6b655c] mt-1">Otomatik (değiştirilemez)</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">Boy (cm) *</label>
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    max={customModal ? customModal.stock_amount * 100 : undefined}
                    value={customLength}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setCustomLength(e.target.value)}
                    placeholder={`Max ${customModal ? fmtNum(customModal.stock_amount * 100) : 0} cm`}
                    data-testid="modal-boy-input"
                  />
                  <p className="text-[0.65rem] text-[#6b655c] mt-1">Stok: {customModal ? fmtNum(customModal.stock_amount * 100) : 0} cm</p>
                </div>
              </div>
              {customModal && customLength && parseFloat(customLength) > customModal.stock_amount * 100 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  Stokta {fmtNum(customModal.stock_amount * 100)} cm var. Lütfen bu değerden daha düşük bir boy giriniz.
                </div>
              )}
            </div>

            {customModal && customLength && parseFloat(customLength) > 0 && parseFloat(customLength) <= customModal.stock_amount * 100 && (
              <div className="bg-[#fdf9f1] border border-[#c8a97e]/40 p-3 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-[#6b655c]">En</div>
                  <div className="font-medium">{customModal.width_cm} cm</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-[#6b655c]">Boy</div>
                  <div className="font-medium">{fmtNum(customLength)} cm</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-[#c8a97e]">Toplam m²</div>
                  <div className="font-serif text-xl">{fmtNum(customSqm)}</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 mt-4 pt-4 border-t border-[#eae5dc]">
            <Button variant="outline" className="flex-1" onClick={() => setCustomModal(null)}>İptal</Button>
            <Button
              onClick={submitCustom}
              disabled={customSubmitting}
              className="btn-luxe flex-1 justify-center"
              data-testid="modal-submit-btn"
            >
              {customSubmitting ? <Loader2 className="animate-spin" size={14}/> : "Sipariş Ver"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SegOpt = ({ label, options, value, onChange, testid }) => (
  <div>
    <div className="label-tag mb-2">{label}</div>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`px-3 py-2 text-sm border transition-colors ${
            value === o
              ? "bg-[#1a1a1a] text-[#f7f5f0] border-[#1a1a1a]"
              : "bg-white border-[#eae5dc] hover:border-[#c8a97e]"
          }`}
          data-testid={`${testid}-${o}`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);
